import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { AccessControlModal } from "components/modals/access-control-modal";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
  TooltipWrapper,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import CustomTable from "components/shared/table";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { deleteBatchJobs } from "data/mutations";
import { fetchBatches } from "data/queries";
import batchDark from "icons/batch-dark.png";
import batchLight from "icons/batch-light.png";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { v4 as uuidv4 } from "uuid";
import { buildColumns } from "./columns-row";
import {
  batchPermissionKeys,
  isPermittedLegacy,
  premiumBatchPermissionKeys,
  ProcessBatchRow,
  SelectedNamesList,
} from "./utils";

export const BatchPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const {
    isPermitted,
    permissionDeniedMessageString,
    isPermissionsSuccess,
    isPermissionsLoading,
  } = usePermissions();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const batchesQuery = useQuery({
      queryKey: [QueryKeys.batchesKey, identityId],
      queryFn: () => fetchBatches(),
      refetchInterval: 5000,
    });

  const isLoading = batchesQuery.isPending || isPermissionsLoading;
  const isSuccess = batchesQuery.isSuccess || isPermissionsSuccess;

  const batchJobDeleteMutation = useMutation({
      mutationFn: () => deleteBatchJobs(selectedIds),
      onSuccess: async (response) => {
          await batchesQuery.refetch();
          setDeleteModalIsOpen(false);
        },
    });

  const rows = batchesQuery.isSuccess
    ? batchesQuery.data
        .map((batchConfig: any) => ProcessBatchRow(batchConfig))
        .filter((batchConfig: any) => {
          return (
            batchConfig.displayName.toLowerCase().includes(searchQuery) ||
            batchConfig.id.toLowerCase().includes(searchQuery)
          );
        })
    : [];

  const hasPremiumJobHistory = rows
    .map((row: any) => row.isPremium)
    .some(Boolean);

  const columns: GridColDef[] = buildColumns({
    batchesQuery,
    identityId,
    identityState,
    queryClient,
  });

  // Show this to the user if they do not have a sub and do not have a history of jobs
  const NoSubNoHistoryContent = () => {
    return (
      <Box>
        <List sx={{ listStyleType: "disc", pl: 4, pt: 0, pb: 0 }}>
          <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
            <ListItemText
              primaryTypographyProps={{ variant: "body2" }}
              primary="Validate, geocode, and enrich your addresses or"
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
            <ListItemText
              primaryTypographyProps={{ variant: "body2" }}
              primary="Reverse geocode locations to addresses"
            />
          </ListItem>
        </List>

        <Typography variant="body2">
          Using G-NAF and G-NAF Live, Australia's most up-to-date national
          address source.
        </Typography>

        <Typography variant="body2">
          Batch Premium can additionally return insights on:
        </Typography>

        <List sx={{ listStyleType: "disc", pl: 6, pt: 0, pb: 0 }}>
          <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
            <ListItemText
              primaryTypographyProps={{ variant: "body2" }}
              primary="Solar"
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
            <ListItemText
              primaryTypographyProps={{ variant: "body2" }}
              primary="Property"
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
            <ListItemText
              primaryTypographyProps={{ variant: "body2" }}
              primary="And more"
            />
          </ListItem>
        </List>

        <Typography variant="body2">
          Subscribe to get started with Batch
        </Typography>
      </Box>
    );
  };

  const batchContent = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <ReactQueryErrorWrapper
          queries={[batchesQuery]}
          mutations={[batchJobDeleteMutation]}
        />

        {/* Display alert if user does not have premium */}
        <PermissionsWrapper
          keys={premiumBatchPermissionKeys}
          messages={{
            [PermissionsReasons.ROLE_DENY]: <></>,
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <Alert
                variant="outlined"
                severity="info"
                action={
                  <UpgradeSubscriptionButton
                    variant="text"
                    text="see subscriptions"
                  />
                }
              >
                <AlertTitle>Upgrade for premium</AlertTitle>
                Want more from Batch? Upgrade to a paid plan for larger credit
                balances and access to premium data such as Solar, Property, and
                Parcel/Cadastre.
              </Alert>
            ),
          }}
        >
          <></>
        </PermissionsWrapper>

        {/* Display alert if user does not have premium but they do have premium jobs */}
        {hasPremiumJobHistory && (
          <PermissionsWrapper
            keys={premiumBatchPermissionKeys}
            messages={{
              [PermissionsReasons.ROLE_DENY]: <></>,
              [PermissionsReasons.UPGRADE_REQUIRED]: (
                <Alert
                  variant="outlined"
                  severity="info"
                  action={
                    <UpgradeSubscriptionButton
                      variant="text"
                      text="see subscriptions"
                    />
                  }
                >
                  <AlertTitle>Premium Batch jobs unavailable</AlertTitle>
                  Looks like some of your batch jobs use premium data. To access
                  them again or create new ones with premium data, please
                  upgrade to a paid plan.
                </Alert>
              ),
            }}
          >
            <></>
          </PermissionsWrapper>
        )}

        <Stack
          sx={{
            gap: "16px",
          }}
        >
          <Card
            sx={{
              padding: "8px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <TextField
              size="small"
              id="batch-search"
              label="Search"
              variant="outlined"
              onChange={(event) =>
                setSearchQuery(event.target.value.toLowerCase())
              }
              value={searchQuery}
              sx={{ width: "300px" }}
            />
            <Box sx={{ display: "flex", gap: "16px" }}>
              <TooltipWrapper
                title={permissionDeniedMessageString([
                  PermissionsKeys.batchDelete,
                ])}
              >
                <Button
                  size="medium"
                  variant="contained"
                  endIcon={<DeleteIcon />}
                  onClick={() => {
                    setDeleteModalIsOpen(true);
                  }}
                  disabled={selectedIds.length > 0 ? false : true}
                >
                  Delete Job
                </Button>
              </TooltipWrapper>

              <PermissionTooltipButton
                permissionKeys={[PermissionsKeys.batchCreate]}
                size="medium"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() =>
                  history.push(Routes.batchCreateBatch, { batchId: uuidv4() })
                }
              >
                New Job
              </PermissionTooltipButton>
            </Box>

            <ConfirmationModal
              isLoading={
                batchJobDeleteMutation.isPending || batchesQuery.isPending
              }
              isError={batchJobDeleteMutation.isError}
              open={deleteModalIsOpen}
              header={`Delete ${selectedIds.length} job${selectedIds.length === 1 ? '' : 's'}?`}
              body={`Are you sure you want to completely delete ${selectedIds.length} jobs?`}
              body2={
                "This will delete the input and output files along with all related batch job information."
              }
              leftButtonText={"CANCEL"}
              leftButtonColor="primary"
              rightButtonText={"DELETE JOBS"}
              rightButtonColor="error"
              handleLeftButton={() => setDeleteModalIsOpen(false)}
              handleRightButton={() => batchJobDeleteMutation.mutate()}
              handleClose={() => setDeleteModalIsOpen(false)}
              type={"Batch"}
              children={<SelectedNamesList selectedNames={selectedNames} />}
            />
          </Card>

          <CustomTable
            rows={rows}
            cols={columns}
            checkboxSelection={true}
            onSelectionModelChange={(ids) => {
              setSelectedIds(ids as string[]);

              const selectedRowsDataNames = ids.map(
                (selectedRowId) =>
                  rows.find((row: any) => row.id === selectedRowId).displayName
              );
              setSelectedNames(selectedRowsDataNames);
            }}
            isRowSelectable={(params: any) => {
              // Can't select running jobs
              if (params.row.status === "Running") {
                return false;
              }

              // Check legacy permissions
              if (
                isPermittedLegacy(
                  params.row.subscriptionProductKey,
                  params.row.status
                )
              ) {
                return true;
              }

              // Check batch delete permissions (required for selection/deletion)
              if (!isPermitted([PermissionsKeys.batchDelete])) {
                return false;
              }

              // Check premium data delete permissions for premium jobs
              if (
                params.row.isPremium &&
                !isPermitted([PermissionsKeys.premiumDataDelete])
              ) {
                return false;
              }

              return true;
            }}
            isLoading={batchesQuery.isPending}
            overlayText={"No batch jobs"}
            getCustomRowClass={(params) => {
              // Only disable entire row if user can't read the data at all
              const hasLegacyAccess = isPermittedLegacy(
                params.row.subscriptionProductKey,
                params.row.status
              );
              
              const readPermissionKeys = params.row.isPremium
                ? [PermissionsKeys.premiumDataRead]
                : [PermissionsKeys.batchRead];
              
              const hasReadAccess = isPermitted(readPermissionKeys);
              
              return !hasLegacyAccess && !hasReadAccess 
                ? "Mui-disabled-row" 
                : "";
            }}
          />
        </Stack>
      </Box>
    );
  };

  return (
    <Box>
      {/* Loading */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Skeleton variant="rectangular" height={58} />
          <Skeleton variant="rectangular" height={576} />
        </Box>
      )}

      {/* No batch history */}
      {batchesQuery.isSuccess && batchesQuery.data.length == 0 && (
        <PermissionsWrapper
          keys={batchPermissionKeys}
          messages={{
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <AccessControlModal
                title="Subscribe to access Batch"
                children={NoSubNoHistoryContent()}
                image={isDark ? batchDark : batchLight}
              />
            ),
          }}
          fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
        >
          {batchContent()}
        </PermissionsWrapper>
      )}

      {/* Has batch history */}
      {batchesQuery.isSuccess && batchesQuery.data.length > 0 && batchContent()}
    </Box>
  );
};
